import React from "react"
import PropTypes from "prop-types"
import { Flex, Skeleton } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { BLACKOUT_SCOPES } from "../../constants"
import styles from "./AffectsPanel.module.css"

/**
 * The impact preview. Business blackouts state a fixed consequence; the other scopes show a
 * live count of the listings the blackout would take offline.
 *
 * The count is stated but never itemised: `impact.listings` is paginated, so listing it would
 * show ten entries under a heading saying seventeen.
 */
const AffectsPanel = ({ scope, impact, isLoading, hasTargets, onViewBookings }) => {
  const { t } = useTranslation()

  /**
   * The conflicting count, not the upcoming one: what the guide needs to act on is the bookings
   * this blackout would break, not every booking that happens to fall in the window.
   */
  const bookingsCount = impact?.conflicting_bookings_count || 0
  const isBusinessScope = scope === BLACKOUT_SCOPES.BUSINESS

  // The panel wrapper stays mounted in every state so its surface and height never flash
  // while the preview re-fetches on each target or date change.
  if (isLoading) {
    return (
      <div className={styles.panel}>
        <Skeleton active paragraph={{ rows: 1 }} title={false} className={styles.skeleton} />
      </div>
    )
  }

  if (!isBusinessScope && !hasTargets) {
    return (
      <Flex align="center" gap="var(--spacing-xs)" className={styles.panel}>
        <Icon name="Info" width={16} height={16} />
        <Text variant="caption" size="lg" className={styles.summaryText}>
          {t("settings.blackout.affects_empty", { defaultValue: "Select targets to preview the impact" })}
        </Text>
      </Flex>
    )
  }

  const summary = isBusinessScope
    ? t("settings.blackout.affects_business", { defaultValue: "All listings, crew and equipment" })
    : t("settings.blackout.affects_listings", { defaultValue: "Affects {{count}} listings", count: impact?.listings_count || 0 })

  return (
    <div className={styles.panel}>
      <Flex align="center" gap="var(--spacing-xs)" className={styles.summaryRow}>
        <Icon name="Info" width={16} height={16} />
        <Text variant="caption" size="lg" className={styles.summaryText}>
          {summary}
        </Text>
        <span className={styles.separator}>·</span>
        <button type="button" className={styles.bookingsLink} onClick={onViewBookings}>
          {t("settings.blackout.conflicting_bookings", { defaultValue: "{{count}} conflicting bookings", count: bookingsCount })}
          <Icon name="ExternalLink" width={12} height={12} />
        </button>
      </Flex>
    </div>
  )
}

AffectsPanel.propTypes = {
  scope: PropTypes.string.isRequired,
  impact: PropTypes.shape({
    listings_count: PropTypes.number,
    conflicting_bookings_count: PropTypes.number
  }),
  isLoading: PropTypes.bool,
  hasTargets: PropTypes.bool,
  onViewBookings: PropTypes.func.isRequired
}

export default AffectsPanel
